import { useCallback } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";

import { getBreadcrumbsBackUrl } from "components/Breadcrumbs/helpers";
import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import CopyFieldDropdownMenuItem from "components/CopyField/DropdownMenuItem";
import DeploymentProgressCallout from "components/DeploymentProgressCallout";
import NotFoundPage from "components/error/NotFoundPage";
import { Stack } from "components/icons/generated";
import PageLoading from "components/loading/PageLoading";
import MetaInfoList from "components/MetaInfoList";
import MetaInfoListItem from "components/MetaInfoList/Item";
import TimestampMetaInfo from "components/Timestamp/MetaInfo";
import ViewHeader from "components/ViewHeader";
import ViewHeaderNavigation from "components/ViewHeader/Navigation";
import ViewHeaderScrollCollapse from "components/ViewHeader/ScrollCollapse";
import ViewHeaderTitle from "components/ViewHeader/Title";
import ViewHeaderWrapper from "components/ViewHeader/Wrapper";
import DropdownMenuDivider from "ds/components/DropdownMenu/Divider";
import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import Tab from "ds/components/Tab";
import useErrorHandle from "hooks/useErrorHandle";
import useTypedContext from "hooks/useTypedContext";
import useCanUpdateTemplateDeployment from "shared/Templates/access/useCanUpdateTemplateDeployment";
import useCanDeleteDeployment from "shared/Templates/access/useCanDeleteDeployment";
import { BlueprintDeploymentState } from "types/generated";

import { TemplateContext } from "../Template/Context";
import { showDeleteConfirmation } from "../Template/Deployments/DeleteConfirmation";
import DeploymentStateBadge from "../Template/Deployments/StateBadge";
import { showUpdateDrawer } from "../Template/Deployments/UpdateDrawer";
import { FILTERS_ORDER_SETTINGS_KEY } from "../TemplatesWorkbench/constants";
import { BLUEPRINT_DEPLOYMENT_QUERY } from "./gql";
import useTemplateDeployment from "./useTemplateDeployment";

const TemplateDeploymentPage = () => {
  const { template, templateUrl } = useTypedContext(TemplateContext);
  const navigate = useNavigate();
  const { templateId, deploymentId } = useParams<{
    templateId: string;
    deploymentId: string;
  }>();

  const {
    deployment,
    loading: deploymentLoading,
    error: deploymentError,
  } = useTemplateDeployment({
    blueprintId: templateId!,
    deploymentId: deploymentId!,
  });

  const canUpdateTemplateDeployment = useCanUpdateTemplateDeployment();
  const canDeleteDeployment = useCanDeleteDeployment();

  const handleUpdate = useCallback(() => {
    if (!deployment) {
      return;
    }

    showUpdateDrawer({
      deployment,
      templateId: template.id,
      refetchQueries: [BLUEPRINT_DEPLOYMENT_QUERY],
    });
  }, [deployment, template.id]);

  const handleDelete = useCallback(() => {
    if (!deployment) {
      return;
    }

    showDeleteConfirmation({
      name: deployment.name,
      id: deployment.id,
      blueprintID: template.id,
      onDelete: () => {
        navigate(`${templateUrl}/deployments`);
      },
    });
  }, [deployment, navigate, templateUrl, template.id]);

  useBreadcrumbs(
    [
      {
        title: "Templates Workbench",
        link: getBreadcrumbsBackUrl("/templates-workbench", FILTERS_ORDER_SETTINGS_KEY),
      },
      {
        title: template?.name || "",
        link: `${templateUrl}/deployments`,
      },
      {
        title: deployment?.name || "",
      },
    ],
    [template?.name, templateUrl, deployment?.name]
  );

  const deploymentErrorContent = useErrorHandle(deploymentError);

  if (deploymentErrorContent) {
    return deploymentErrorContent;
  }

  if (deploymentLoading && !deployment) {
    return <PageLoading />;
  }

  if (!template || !deployment) {
    return <NotFoundPage />;
  }

  const deploymentUrl = `/template/${templateId}/deployment/${deployment.id}`;

  const updateAccess = canUpdateTemplateDeployment(template.space.id, deployment.state);
  const deleteAccess = canDeleteDeployment(deployment, template.space.id);

  const isInProgress =
    deployment.state === BlueprintDeploymentState.InProgress ||
    deployment.state === BlueprintDeploymentState.None;

  return (
    <>
      <ViewHeader>
        <ViewHeaderWrapper direction="row" justify="between" fullWidth>
          <ViewHeaderWrapper direction="row" align="center">
            <ViewHeaderTitle>{deployment.name}</ViewHeaderTitle>
            <DeploymentStateBadge state={deployment.state} />
          </ViewHeaderWrapper>

          <ViewHeaderWrapper direction="row" align="end" shrink="0">
            <DropdownMenuEllipsis tooltip="Deployment actions" dotsSize="medium">
              <DropdownMenuItem
                onAction={handleUpdate}
                isDisabled={!updateAccess.result}
                tooltip={updateAccess.message}
              >
                Update version
              </DropdownMenuItem>
              <DropdownMenuDivider />
              <CopyFieldDropdownMenuItem title="Copy ID" value={deployment.id} />
              <DropdownMenuDivider />
              <DropdownMenuItem
                onAction={handleDelete}
                danger
                isDisabled={!deleteAccess.result}
                tooltip={deleteAccess.message}
              >
                Delete
              </DropdownMenuItem>
            </DropdownMenuEllipsis>
          </ViewHeaderWrapper>
        </ViewHeaderWrapper>

        <ViewHeaderScrollCollapse>
          <MetaInfoList>
            <MetaInfoListItem icon={Stack}>{deployment.blueprintVersion.version}</MetaInfoListItem>

            <TimestampMetaInfo timestamp={deployment.createdAt} />
          </MetaInfoList>
        </ViewHeaderScrollCollapse>

        <ViewHeaderNavigation>
          <Tab end to={`${deploymentUrl}`} label="Stacks" />
          <Tab to={`${deploymentUrl}/outputs`} label="Outputs" />
        </ViewHeaderNavigation>
      </ViewHeader>
      {isInProgress && <DeploymentProgressCallout />}

      <Outlet context={{ deployment }} />
    </>
  );
};

export default TemplateDeploymentPage;
